import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { toast } from "react-toastify";
import { isEmpty } from "lodash";

import {
  masterConfigItemList,
  masterConfigItemCreate,
  masterConfigItemCreateSuccess,
  masterConfigItemUpdate,
  masterConfigItemUpdateSuccess,
} from "../../../../store/masterConfigItem/action";
import SettingsPanel from "../../components/SettingsPanel";

// Reference token the rest of the code/frontend uses to look a config item up
// (resolved value by MasterConfigItem.Id). Auto-generated from the Id.
const configReference = (id) => `ZCONFIGVAL_${id}_X`;

const MasterConfigItem = () => {
  const dispatch = useDispatch();

  const {
    masterConfigItemListAPI,
    masterConfigItemListLoading,
    createSuccessAPI,
    updateSuccessAPI,
  } = useSelector((state) => ({
    masterConfigItemListAPI:
      state.masterConfigItemReducer.masterConfigItemList?.masterconfigitem_list,
    masterConfigItemListLoading:
      state.masterConfigItemReducer.masterConfigItemListLoading,
    createSuccessAPI: state.masterConfigItemReducer.masterConfigItemCreate,
    updateSuccessAPI: state.masterConfigItemReducer.masterConfigItemUpdate,
  }));

  const rows = (masterConfigItemListAPI || []).map((item) => ({
    ...item,
    id: item.Id,
  }));

  const initialFormState = {
    Key: "",
    Title: "",
    Description: "",
    ValueVarChar: "",
    FlagDefineLocalStorage: "0",
    FlagAllowUserOverride: "0",
    FlagAllowFrontEndEdit: "0",
  };

  const [formData, setFormData] = useState(initialFormState);
  const [openDialog, setOpenDialog] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editId, setEditId] = useState(null);

  useEffect(() => {
    dispatch(masterConfigItemList());
  }, [dispatch]);

  const handleOpenDialog = (item = null) => {
    if (item && item.Id) {
      setIsEditing(true);
      setEditId(item.Id);
      setFormData({
        Key: item.Key || "",
        Title: item.Title || "",
        Description: item.Description || "",
        ValueVarChar: item.ValueVarChar || "",
        FlagDefineLocalStorage: item.FlagDefineLocalStorage || "0",
        FlagAllowUserOverride: item.FlagAllowUserOverride || "0",
        FlagAllowFrontEndEdit: item.FlagAllowFrontEndEdit || "0",
      });
    } else {
      setIsEditing(false);
      setEditId(null);
      setFormData(initialFormState);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setFormData(initialFormState);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFlagChange = (e) => {
    const { name, checked } = e.target;
    setFormData((prev) => ({ ...prev, [name]: checked ? "1" : "0" }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (isEditing) {
      dispatch(masterConfigItemUpdate([{ Id: editId, ...formData }]));
    } else {
      dispatch(masterConfigItemCreate([{ ...formData }]));
    }
    handleCloseDialog();
  };

  const copyReference = (id) => {
    navigator.clipboard?.writeText(configReference(id));
    toast.success(`Copied ${configReference(id)}`);
  };

  // Create success -> refresh + surface the generated reference token
  useEffect(() => {
    if (isEmpty(createSuccessAPI)) return;
    if (createSuccessAPI?.status === 200) {
      const newId = createSuccessAPI?.masterconfigitem_id;
      toast.success(
        newId
          ? `Created. Reference: ${configReference(newId)}`
          : createSuccessAPI?.message
      );
      dispatch(masterConfigItemList());
    } else {
      toast.error(createSuccessAPI?.message);
    }
    dispatch(masterConfigItemCreateSuccess({}));
  }, [createSuccessAPI, dispatch]);

  // Update success -> refresh
  useEffect(() => {
    if (isEmpty(updateSuccessAPI)) return;
    if (updateSuccessAPI?.status === 200) {
      toast.success(updateSuccessAPI?.message);
      dispatch(masterConfigItemList());
    } else {
      toast.error(updateSuccessAPI?.message);
    }
    dispatch(masterConfigItemUpdateSuccess({}));
  }, [updateSuccessAPI, dispatch]);

  const flagRenderer = (params) => (params.value === "1" ? "Yes" : "No");

  const columns = [
    { field: "Id", headerName: "ID", width: 70 },
    {
      field: "Reference",
      headerName: "Reference",
      width: 190,
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <code>{configReference(params.row.Id)}</code>
          <Tooltip title="Copy reference">
            <IconButton size="small" onClick={() => copyReference(params.row.Id)}>
              <ContentCopyIcon fontSize="inherit" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
    { field: "Key", headerName: "Key", flex: 1, minWidth: 160 },
    { field: "Title", headerName: "Title", flex: 1, minWidth: 180 },
    { field: "ValueVarChar", headerName: "Default", width: 100 },
    {
      field: "FlagDefineLocalStorage",
      headerName: "Local Storage",
      width: 120,
      renderCell: flagRenderer,
    },
    {
      field: "FlagAllowUserOverride",
      headerName: "User Override",
      width: 120,
      renderCell: flagRenderer,
    },
    {
      field: "FlagAllowFrontEndEdit",
      headerName: "Front-End Edit",
      width: 120,
      renderCell: flagRenderer,
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 90,
      sortable: false,
      renderCell: (params) => (
        <IconButton
          color="primary"
          size="small"
          onClick={() => handleOpenDialog(params.row)}
        >
          <EditIcon />
        </IconButton>
      ),
    },
  ];

  return (
    <SettingsPanel
      title="Config Items"
      description="Master config item definitions (ZCRM_Master). Add or edit the items every company inherits. Code references each item by its generated ZCONFIGVAL_<Id>_X token."
    >
      <Box sx={{ p: 3, width: "100%", minWidth: 0, maxWidth: "100%" }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
          <Typography variant="h5" component="h1" fontWeight="bold">
            Master Config Items
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Add Config Item
          </Button>
        </Box>

        <Paper elevation={3} sx={{ height: 600, width: "100%" }}>
          <DataGrid
            rows={rows}
            columns={columns}
            loading={masterConfigItemListLoading}
            pageSizeOptions={[10, 25, 50]}
            initialState={{
              pagination: { paginationModel: { pageSize: 25 } },
            }}
            disableRowSelectionOnClick
            sx={{ border: 0 }}
          />
        </Paper>

        <Dialog
          open={openDialog}
          onClose={handleCloseDialog}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            {isEditing
              ? `Edit Config Item (${configReference(editId)})`
              : "Add New Config Item"}
          </DialogTitle>
          <form onSubmit={handleSubmit}>
            <DialogContent dividers>
              {!isEditing && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  The Id is assigned automatically on save; its ZCONFIGVAL_&lt;Id&gt;_X
                  reference is shown afterwards so you can wire it into code.
                </Typography>
              )}
              <TextField
                margin="dense"
                label="Key (machine name, e.g. timer_stop_note_required)"
                name="Key"
                value={formData.Key}
                onChange={handleInputChange}
                fullWidth
                required
              />
              <TextField
                margin="dense"
                label="Title"
                name="Title"
                value={formData.Title}
                onChange={handleInputChange}
                fullWidth
                required
              />
              <TextField
                margin="dense"
                label="Description (what it means)"
                name="Description"
                value={formData.Description}
                onChange={handleInputChange}
                fullWidth
                multiline
                rows={3}
              />
              <TextField
                margin="dense"
                label="Default value (ValueVarChar)"
                name="ValueVarChar"
                value={formData.ValueVarChar}
                onChange={handleInputChange}
                fullWidth
              />
              <Box sx={{ mt: 1 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      name="FlagDefineLocalStorage"
                      checked={formData.FlagDefineLocalStorage === "1"}
                      onChange={handleFlagChange}
                    />
                  }
                  label="Deliver to client at login (Local Storage)"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      name="FlagAllowUserOverride"
                      checked={formData.FlagAllowUserOverride === "1"}
                      onChange={handleFlagChange}
                    />
                  }
                  label="Allow per-staff override"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      name="FlagAllowFrontEndEdit"
                      checked={formData.FlagAllowFrontEndEdit === "1"}
                      onChange={handleFlagChange}
                    />
                  }
                  label="Show on company Config Items screen (front-end editable)"
                />
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseDialog} color="inherit">
                Cancel
              </Button>
              <Button type="submit" variant="contained" color="primary">
                {isEditing ? "Save Changes" : "Create"}
              </Button>
            </DialogActions>
          </form>
        </Dialog>
      </Box>
    </SettingsPanel>
  );
};

export default MasterConfigItem;
